import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { IntelligenceCard, IntelligenceCardStatus } from './IntelligenceCard';
import { useActiveStudentsNotEnrolled } from '@/hooks/useActiveStudentsNotEnrolled';

export const ActiveStudentsNotEnrolledCard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useActiveStudentsNotEnrolled();

  // This is always a growth opportunity - success state
  const getStatus = (count: number): IntelligenceCardStatus => {
    if (count === 0) return 'neutral';
    return 'success'; // Growth opportunities are positive!
  };

  const handleExpandEnrollment = () => {
    navigate('/admin/students', { 
      state: { 
        filterExpansionOpportunity: true,
        suggestEnrollment: true
      } 
    });
  };

  const count = data?.count || 0;
  const status = getStatus(count);

  const getContextText = () => {
    if (count === 0) {
      return "All active students are fully enrolled! 🎓";
    }
    
    if (count === 1) {
      const student = data?.students[0];
      return `${student?.first_name} ${student?.last_name} - ${student?.engagement_score}% engagement`;
    }
    
    if (count <= 3) {
      const topStudent = data?.students.reduce((prev, current) => 
        current.engagement_score > prev.engagement_score ? current : prev
      );
      return `${topStudent?.first_name} leads with ${topStudent?.engagement_score}% engagement`;
    }
    
    const avgEngagement = Math.round(
      (data?.students.reduce((sum, student) => sum + student.engagement_score, 0) || 0) / count
    );
    
    return `${avgEngagement}% average engagement - ready for more classes!`;
  };

  const getStatusText = () => {
    if (count === 0) return 'Fully enrolled';
    if (count === 1) return '1 expansion opportunity';
    return `${count} expansion opportunities`;
  };

  return (
    <IntelligenceCard
      title="Active Students Not Enrolled"
      description="Engaged students ready for more classes"
      icon={UserPlus}
      metric={count}
      metricLabel={count === 1 ? "unenrolled student" : "unenrolled students"}
      contextText={getContextText()}
      status={status}
      statusText={getStatusText()}
      actionLabel={count > 0 ? "Expand Enrollment" : "Review Students"}
      onAction={handleExpandEnrollment}
      actionDisabled={false}
      isLoading={isLoading}
      error={error?.message}
    />
  );
};